import {
    useMemo,
} from 'react'
import flow from 'lodash/flow'

import {
    useArchivedTableControl,
} from 'app/shared-components/TableControls/ShowArchived'
import injectOnLoad from 'app/Apps/DomainObject/utils/injectOnLoad'
import injectSearchQuery from 'app/Apps/DomainObject/utils/injectSearchQuery'
import injectSort from 'app/Apps/DomainObject/utils/injectSort'
import injectParseResponse from 'app/Apps/DomainObject/utils/injectParseResponse'
import {
    TableResponse,
} from 'app/types/request.types'
import injectDefaultFilters from 'app/Apps/DomainObject/utils/injectDefaultFilters'

import {
    SkyNetTableSort,
    SkyNetTableTabWithLabel,
} from '../types/skyNetTable.types'

import useTableTab from './useTableTab'
import useLoadDtoForSkyNetTable from './useLoadDtoForSkyNetTable'
import injectUrlQuery from './utils/injectUrlQuery'
import injectArchived from './utils/injectArchived'

type Params = {
    customUrl?: string,
    filter?: Record<string, any>,
    filters?: Record<string, any>,
    method?: string,
    name: string,
    moduleName?: string,
    onLoadData: (data: TableResponse)=> void,
    rowsPerPageDefault?: number,
    tabs: SkyNetTableTabWithLabel[],
    searchValue: string,
    paginationModeMatches?: boolean,
    parseResponse: (data: TableResponse)=> TableResponse,
    defaultSort?: SkyNetTableSort,
}

const useLoadWithParams = ({
    customUrl,
    filter,
    filters: filtersFromTable,
    method,
    name,
    moduleName,
    onLoadData,
    rowsPerPageDefault,
    tabs,
    searchValue,
    paginationModeMatches,
    parseResponse,
    defaultSort,
}: Params) => {
    const showArchived = useArchivedTableControl(name)
    const selected = useTableTab({
        tabs,
    })

    const {
        url: urlQuery,
        includeFilters,
        filters,
        customUrl: selectedTabCustomUrl,
    } = useMemo((): SkyNetTableTabWithLabel => {
        if (selected?.requestUrl) {
            return {
                ...selected,
                url: selected.requestUrl,
            }
        }

        return selected || {
            url: undefined, name: undefined, label: undefined, fields: [],
        }
    }, [selected])

    const appliedFilter = useMemo((): Record<string, any> => {
        return {
            ...includeFilters,
            ...filter,
        }
    }, [
        includeFilters,
        filter,
    ])

    const appliedFilters = useMemo(() => {
        return filtersFromTable || filters ? {
            ...filtersFromTable,
            ...filters,
        } : undefined
    }, [
        filters,
        filtersFromTable,
    ])

    const load = useLoadDtoForSkyNetTable({
        name: moduleName || name,
        version: undefined,
        rowsPerPage: rowsPerPageDefault,
        customUrl: selectedTabCustomUrl || customUrl,
        method,
        paginationModeMatches,
    })

    const loadWithParams = useMemo((): (params: any)=>any => {
        return flow([
            injectSort(defaultSort),
            injectDefaultFilters(appliedFilters),
            injectUrlQuery(urlQuery),
            injectSearchQuery(searchValue),
            injectArchived(showArchived),
            injectOnLoad(onLoadData),
            injectParseResponse(parseResponse),
        ])(load)
    }, [
        defaultSort,
        appliedFilters,
        urlQuery,
        searchValue,
        showArchived,
        onLoadData,
        parseResponse,
        load,
    ])

    return {
        loadWithParams,
        appliedFilter,
        selectedTabUrlQuery: urlQuery,
        selectedTabCustomUrl,
    }
}

export default useLoadWithParams
